package top.wcpe.allincore.ui.runtime.animation

import top.wcpe.allincore.dsl.ir.expr.ExprBlobReader
import top.wcpe.allincore.dsl.ir.model.UiBinding
import top.wcpe.allincore.dsl.ir.model.animation.UiAnimation
import top.wcpe.allincore.dsl.ir.model.statemachine.UiSmTransition
import top.wcpe.allincore.dsl.ir.model.statemachine.UiStateMachine
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.concurrent.SyncLock
import top.wcpe.allincore.ui.runtime.internal.concurrent.withLock
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import top.wcpe.allincore.ui.runtime.ir.prop.PropAppliers

/**
 * 状态机运行时实例（M10b）。
 *
 * 每个引用了 [UiStateMachine] 的元素持有一个独立实例：定义可被多个元素共享，但 currentState 互相隔离。
 * [transitionTo] 复用 [AnimationScheduler.transition] 把目标状态下每个属性从当前实时值插值到 target
 * state 中声明的值。
 *
 * transition 查找规则：`(from=current, to=target)` 精确优先，其次 `(from=null, to=target)` 兜底，
 * 否则抛出 [IllegalStateException]。
 *
 * @param scheduler 允许 null：未挂载调度器时，transition 无法插值，直接静态写入
 * @param evalCtx   允许 null：state 块仅含字面量时可省
 */
class StateMachineInstance(
    private val def: UiStateMachine,
    private val element: UIElement<*>,
    private val scheduler: AnimationScheduler?,
    private val evalCtx: EvalContext?,
) {

    /** 原 @Synchronized（锁 this）方法的平台中立替代：类内单锁互斥（语义同粗粒度对象锁）。 */
    private val syncMonitor = SyncLock()

    private var currentStateName: String = def.initialState

    fun currentState(): String = syncMonitor.withLock { currentStateName }

    fun definition(): UiStateMachine = def

    /**
     * 切换到目标状态：按规则查找 transition 模板，对 target state 中声明的每个属性做插值。
     * 找不到 transition 抛 [IllegalStateException]；目标 state 未声明抛 [IllegalArgumentException]。
     */
    fun transitionTo(target: String) {
        syncMonitor.withLock {
            val targetState = def.states[target]
                ?: throw IllegalArgumentException("状态机 ${def.id} 未声明状态: $target")
            val template = asTemplate(lookup(currentStateName, target))
            val props = targetState.props
            when {
                scheduler != null && props.isNotEmpty() ->
                    for ((prop, binding) in props) {
                        val to = evalBinding(binding)
                        val from = PropAppliers.read(element, prop)
                        scheduler.transition(element, prop, from, to, template)
                    }
                // 无调度器时静态写入，确保 transitionTo 至少把目标值落到元素上。
                props.isNotEmpty() ->
                    for ((prop, binding) in props) {
                        PropAppliers.apply(element, prop, evalBinding(binding))
                    }
            }
            currentStateName = target
        }
    }

    /** 精确匹配 (from, to) 优先；其次 (from=null, to) 兜底；否则抛异常。 */
    private fun lookup(from: String?, to: String): UiSmTransition {
        var fallback: UiSmTransition? = null
        for (tr in def.transitions) {
            if (tr.to != to) continue
            if (tr.from == from) return tr
            if (tr.from == null && fallback == null) fallback = tr
        }
        return fallback
            ?: throw IllegalStateException("状态机 ${def.id} 缺少 transition: $from -> $to")
    }

    private fun evalBinding(binding: UiBinding): Any? = when (binding.type) {
        UiBinding.BindingType.LITERAL -> binding.literalValue
        UiBinding.BindingType.EXPR -> {
            checkNotNull(evalCtx) {
                "状态机 ${def.id} 的 state 块含 EXPR 绑定但未注入 EvalContext"
            }
            // EXPR 绑定保证 exprBytes 非 null；跨模块无法 smart-cast 公开属性，用 !! 复刻旧平台类型非空传递。
            ExprEvaluator.eval(ExprBlobReader.readExpr(binding.exprBytes!!), evalCtx)
        }

        else -> throw IllegalStateException("状态机 state 块不支持的绑定类型: ${binding.type}")
    }

    private companion object {
        /** 把 [UiSmTransition] 编织为 [AnimationScheduler.transition] 所需的 UiAnimation 模板。 */
        fun asTemplate(tr: UiSmTransition): UiAnimation = UiAnimation(
            UiAnimation.Kind.TRANSITION,
            tr.durationMs.toDouble(),
            tr.delayMs.toDouble(),
            0.0,
            tr.easing,
            emptyMap(),
            emptyList(),
            emptyList(),
            null,
        )
    }
}
